import { storiesApi, newsApi, activitiesApi } from '../api/resources';
import { assetUrl } from '../api/client';

/**
 * The three content types that flow through the approval workflow.
 * Each entry maps a queue row back to its API service so approve / reject /
 * publish can be called generically by the Approval Center, and so the shared
 * SubmissionPreview modal can re-fetch a single record when it needs the body.
 *
 * Lives in components/ (not pages/admin/) because both the admin Approval
 * Center and the member "My ..." pages consume it.
 */
export const SUBMISSION_TYPES = {
  stories: {
    key: 'stories',
    label: 'Story',
    plural: 'Stories',
    eyebrow: 'Story',
    api: storiesApi,
    /** Stories only include `body` in list responses when asked for it. */
    listParams: { status: 'pending', per_page: 50, with_body: 1 },
    badgeClass: 'bg-maroon-50 text-maroon-700',
    /** Public detail route for a PUBLISHED item (slug preferred, id fallback). */
    publicPath: (item) => `/stories/${item?.slug || item?.id}`,
  },
  news: {
    key: 'news',
    label: 'News post',
    plural: 'News',
    eyebrow: 'News & Highlights',
    api: newsApi,
    listParams: { status: 'pending', per_page: 50 },
    badgeClass: 'bg-gold-100 text-gold-800',
    publicPath: (item) => `/news/${item?.slug || item?.id}`,
  },
  activities: {
    key: 'activities',
    label: 'Activity',
    plural: 'Activities',
    eyebrow: 'Activity',
    api: activitiesApi,
    listParams: { status: 'pending', per_page: 50 },
    badgeClass: 'bg-blue-100 text-blue-800',
    publicPath: (item) => `/activities/${item?.slug || item?.id}`,
  },
};

export const SUBMISSION_ORDER = ['stories', 'news', 'activities'];

/** Best display URL for a news featured image (absolute path or stored path). */
function newsImage(item) {
  const path = item?.featured_image_path;
  if (path && /^https?:\/\//i.test(path)) return path;
  return item?.featured_image_url || assetUrl(path);
}

/**
 * Flatten a Story / News / Activity API resource into one shape the queue rows
 * and the preview modal both consume:
 *   { key, type, id, slug, title, author, image, excerpt, body, gallery,
 *     category, date, dateLabel, status }
 */
export function normalizeSubmission(raw, type) {
  const base = {
    key: `${type}-${raw?.id}`,
    type,
    id: raw?.id,
    slug: raw?.slug || null,
    title: raw?.title || 'Untitled',
    author: raw?.author?.name || '',
    status: raw?.status || 'pending',
    category: '',
    gallery: [],
  };

  if (type === 'news') {
    return {
      ...base,
      image: newsImage(raw),
      excerpt: raw?.excerpt || '',
      body: raw?.body || '',
      gallery: raw?.gallery || [],
      date: raw?.updated_at || raw?.created_at || null,
      dateLabel: raw?.updated_at ? 'Updated' : 'Created',
    };
  }

  if (type === 'activities') {
    return {
      ...base,
      image: assetUrl(raw?.first_image_url) || assetUrl(raw?.image_path),
      excerpt: raw?.summary || '',
      body: raw?.description || '',
      gallery: raw?.gallery || [],
      date: raw?.updated_at || raw?.occurred_on || null,
      dateLabel: raw?.updated_at ? 'Updated' : 'Dated',
    };
  }

  return {
    ...base,
    image: assetUrl(raw?.featured_image_path),
    excerpt: raw?.excerpt || '',
    body: raw?.body || '',
    category: raw?.category?.name || '',
    date: raw?.updated_at || raw?.created_at || null,
    dateLabel: raw?.updated_at ? 'Updated' : 'Created',
  };
}

/** Newest first; rows without a usable date sink to the bottom. */
export function byNewest(a, b) {
  const ta = a.date ? Date.parse(a.date) : 0;
  const tb = b.date ? Date.parse(b.date) : 0;
  return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
}
